import React, { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

/**
 * Install-as-app prompt for Canales Furniture.
 * - Chromium browsers fire `beforeinstallprompt` — we stash it and surface a button.
 * - iOS Safari does not fire that event; we show a short instructional hint instead.
 * - Dismissals are remembered in localStorage so we don't nag users.
 */
export default function InstallPWA() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    const dismissed = localStorage.getItem("canales_pwa_dismissed") === "1";
    if (dismissed) return;

    const isStandalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (isStandalone) {
      setInstalled(true);
      return;
    }

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    const onInstalled = () => {
      setInstalled(true);
      setVisible(false);
      setDeferred(null);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);

    // Detect iOS (Safari does not fire beforeinstallprompt)
    const ua = window.navigator.userAgent || "";
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(ua);
    if (isIOS && isSafari) {
      setIosHint(true);
      setVisible(true);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice.catch(() => ({ outcome: "dismissed" }));
    if (outcome === "accepted") {
      setInstalled(true);
    }
    setDeferred(null);
    setVisible(false);
  };

  const dismiss = () => {
    localStorage.setItem("canales_pwa_dismissed", "1");
    setVisible(false);
  };

  if (installed || !visible) return null;

  return (
    <div
      data-testid="pwa-install-banner"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] w-[92%] max-w-md"
    >
      <div className="brutal p-4 bg-canales-yellow flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl border-2 border-black bg-canales-blue flex items-center justify-center flex-shrink-0">
          <Download className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-black text-lg leading-tight">
            Instala Canales como app
          </div>
          {iosHint ? (
            <div className="mt-1 font-body text-sm">
              Toca <span className="font-bold">Compartir</span> y elige{" "}
              <span className="font-bold">Añadir a pantalla de inicio</span>.
            </div>
          ) : (
            <div className="mt-1 font-body text-sm">
              Ábrela desde el celular como una app nativa, sin buscar en el navegador.
            </div>
          )}
          {!iosHint && (
            <button
              type="button"
              onClick={install}
              data-testid="pwa-install-btn"
              className="mt-3 brutal-btn bg-canales-red text-white px-4 py-2 text-sm font-display font-black flex items-center gap-2"
            >
              <Download className="w-4 h-4" /> Instalar app
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={dismiss}
          data-testid="pwa-install-dismiss"
          aria-label="dismiss"
          className="text-black/70 hover:text-black"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}
